/**
 * SplitsExplorer - Comprehensive splits analysis component for player detail pages
 * 
 * Tabs:
 *  1. Zone Analysis - InteractiveZoneChart with full filtering (pitch type, hand, game, result)
 *  2. vs LH / vs RH - Side-by-side handedness splits with pitch type breakdown
 *  3. Game Log - Per-game pitch usage trends and stats
 *  4. Matchup Splits - Macro splits: home/away, vs team, vs pitcher/batter, count
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper, Grid, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Skeleton, Alert,
  ToggleButton, ToggleButtonGroup, CircularProgress, useTheme, alpha,
  Card, CardContent, Divider, Stack, LinearProgress, TextField,
  InputAdornment, IconButton, Tooltip, Collapse
} from '@mui/material';
import {
  ArrowUpward, ArrowDownward, Search, ExpandMore, ExpandLess,
  Home, Flight, SwapHoriz, Groups, Shuffle, SportsBaseball,
  History, Person
} from '@mui/icons-material';
import InteractiveZoneChart from './charts/InteractiveZoneChart';
import StrikeZonePlot from './charts/StrikeZonePlot';
import SplitsMatrix from './player/SplitsMatrix';
import { pitchSplitsApi, macroSplitsApi } from '../services/apiService';
import { getPitchName, PITCH_NAMES as PITCH_TYPE_NAMES, PITCH_COLORS } from '../utils/pitchTypes';

// ────────── Subcomponents ──────────

/** Stat card with label/value */
const StatBox = ({ label, value, sub, color }) => (
  <Box sx={{ textAlign: 'center', minWidth: 60 }}>
    <Typography variant="caption" sx={{ display: 'block', opacity: 0.6, fontSize: '0.75rem', lineHeight: 1.2 }}>{label}</Typography>
    <Typography variant="body2" fontWeight="bold" sx={{ color, fontSize: '0.85rem' }}>{value}</Typography>
    {sub && <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '0.75rem' }}>{sub}</Typography>}
  </Box>
);

/** Pitch type row for handedness table */
const PitchTypeRow = ({ code, data, totalPitches }) => {
  const usage = totalPitches > 0 ? ((data.count / totalPitches) * 100).toFixed(1) : 0;
  return (
    <TableRow hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
      <TableCell sx={{ py: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PITCH_COLORS[code] || '#888' }} />
          <Typography variant="caption" fontWeight="bold">{PITCH_TYPE_NAMES[code] || code}</Typography>
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ py: 0.5 }}><Typography variant="caption">{data.count}</Typography></TableCell>
      <TableCell align="right" sx={{ py: 0.5 }}><Typography variant="caption">{usage}%</Typography></TableCell>
      <TableCell align="right" sx={{ py: 0.5 }}><Typography variant="caption">{data.avgVelo > 0 ? data.avgVelo.toFixed(1) : '-'}</Typography></TableCell>
      <TableCell align="right" sx={{ py: 0.5 }}><Typography variant="caption">{data.whiffRate != null ? data.whiffRate.toFixed(1) + '%' : '-'}</Typography></TableCell>
      <TableCell align="right" sx={{ py: 0.5 }}>
        <Typography variant="caption" fontWeight="bold">
          {data.battingAvg != null ? (data.battingAvg >= 1 ? '1.000' : data.battingAvg.toFixed(3).substring(1)) : '-'}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

// ────────── Main Component ──────────
const SplitsExplorer = ({ team, playerName, season, perspective = 'batter', gameTypeFilter, sharedPitchEvents }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Data states
  const [pitchEvents, setPitchEvents] = useState([]);
  const [vsHandL, setVsHandL] = useState(null);
  const [vsHandR, setVsHandR] = useState(null);
  const [gameLog, setGameLog] = useState([]);
  const [macroSplits, setMacroSplits] = useState(null);
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [splitRange, setSplitRange] = useState('season'); // kept for API compat

  // ── Internal splits-specific filters (independent of outer PlayerDetail tabs) ──
  const splitsGameType = 'R'; // Advanced Analytics — Regular Season only
  const [splitsDateRange, setSplitsDateRange] = useState('season'); // 'season' | 'alltime'

  // ── MacroSplitsTab UI state (lifted here to survive game-type filter re-renders) ──
  const [activeSection, setActiveSection] = useState('all');
  const [splitSortBy, setSplitSortBy] = useState('pa');
  const [splitSortOrder, setSplitSortOrder] = useState('desc');
  const [splitSearch, setSplitSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState({ by_location: true, vs_handedness: true, vs_teams: true, vs_pitchers: true, vs_batters: true, by_count: true });
  const [showAllPitchers, setShowAllPitchers] = useState(false);
  const [showAllTeams, setShowAllTeams] = useState(false);

  const safePlayer = playerName?.replace(/\s+/g, '_') || '';
  const isCareer = splitsDateRange === 'alltime';

  // ── Load pitch events ──
  const loadPitchEvents = useCallback(async () => {
    if (!team || !safePlayer) return;
    setLoading(l => ({ ...l, events: true }));
    try {
      const result = await pitchSplitsApi.getPitchEvents(perspective, team, safePlayer, season, { limit: 5000 });
      setPitchEvents(result?.events || []);
    } catch (e) {
      console.error('Failed to load pitch events', e);
      setErrors(prev => ({ ...prev, events: 'Failed to load pitch event data' }));
    } finally {
      setLoading(l => ({ ...l, events: false }));
    }
  }, [team, safePlayer, season, perspective]);

  // ── Load handedness splits ──
  const loadHandedness = useCallback(async () => {
    if (!team || !safePlayer) return;
    setLoading(l => ({ ...l, hand: true }));
    try {
      const [lData, rData] = await Promise.all([
        pitchSplitsApi.getVsHand(perspective, team, safePlayer, season, 'L'),
        pitchSplitsApi.getVsHand(perspective, team, safePlayer, season, 'R'),
      ]);
      setVsHandL(lData);
      setVsHandR(rData);
    } catch (e) {
      console.error('Failed to load handedness splits', e);
      setErrors(prev => ({ ...prev, hand: 'Failed to load handedness data' }));
    } finally {
      setLoading(l => ({ ...l, hand: false }));
    }
  }, [team, safePlayer, season, perspective]);

  // ── Load game log ──
  const loadGameLog = useCallback(async () => {
    if (!team || !safePlayer) return;
    setLoading(l => ({ ...l, games: true }));
    try {
      const result = await pitchSplitsApi.getGameLog(perspective, team, safePlayer, season);
      setGameLog(result?.games || []);
    } catch (e) {
      console.error('Failed to load game log', e);
      setErrors(prev => ({ ...prev, games: 'Failed to load game log' }));
    } finally {
      setLoading(l => ({ ...l, games: false }));
    }
  }, [team, safePlayer, season, perspective]);

  // ── Load macro splits ──
  const loadMacroSplits = useCallback(async () => {
    if (!team || !safePlayer) return;
    setLoading(l => ({ ...l, macro: true }));
    try {
      // Request vs_batters for pitchers, vs_pitchers for batters
      const matchupPath = perspective === 'pitcher' ? 'vs_batters' : 'vs_pitchers';
      const requestPaths = ['by_location', 'vs_handedness', 'vs_teams', matchupPath, 'by_count'];
      // Also request the other matchup path so pitchers see vs_pitchers too
      if (perspective === 'pitcher') requestPaths.push('vs_pitchers');
      const result = await macroSplitsApi.getPlayerMacro(team, safePlayer, season, {
        paths: requestPaths,
        gameType: splitsGameType,
        career: isCareer || undefined,
      });
      setMacroSplits(result);
    } catch (e) {
      console.error('Failed to load macro splits', e);
      setErrors(prev => ({ ...prev, macro: 'Failed to load macro splits' }));
    } finally {
      setLoading(l => ({ ...l, macro: false }));
    }
  }, [team, safePlayer, season, splitsGameType, perspective, isCareer]);

  // ── Pre-compute values that use hooks (must be at top level) ──
  
  // All pitch types across all games (for GameLogTab)
  const allGamePitchTypes = useMemo(() => {
    const types = new Set();
    gameLog.forEach(g => Object.keys(g.pitchTypes || {}).forEach(pt => types.add(pt)));
    return [...types].sort();
  }, [gameLog]);

  // Macro splits by path (for MacroSplitsTab)
  const splitsByPath = useMemo(() => {
    if (!macroSplits) return {};
    if (Array.isArray(macroSplits)) {
      const obj = {};
      macroSplits.forEach(r => { if (r.path && r.data) obj[r.path] = r.data; });
      return obj;
    }
    return macroSplits.splits || macroSplits;
  }, [macroSplits]);

  // Total games covered by splits data (unique gamePks across by_location)
  const splitsTotalGames = useMemo(() => {
    const bl = splitsByPath?.by_location;
    if (!bl || typeof bl !== 'object') return 0;
    const pks = new Set();
    Object.values(bl).forEach(loc => {
      if (loc?.games && Array.isArray(loc.games)) loc.games.forEach(pk => pks.add(pk));
      else if (loc?.gameCount) for (let i = 0; i < loc.gameCount; i++) pks.add(`${Math.random()}`);
    });
    return pks.size;
  }, [splitsByPath]);

  // Load macro splits on mount
  useEffect(() => {
    if (!macroSplits && !loading.macro) loadMacroSplits();
  }, []);

  // Re-load macro splits when internal filters change; also reset section/sort state
  useEffect(() => {
    setMacroSplits(null);
    setActiveSection('all');
    setSplitSearch('');
    setSplitSortBy('pa');
    setSplitSortOrder('desc');
    setShowAllPitchers(false);
    setShowAllTeams(false);
    loadMacroSplits();
  }, [splitsDateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // ────── TAB 0: Zone Analysis ──────
  const ZoneAnalysisTab = () => {
    if (loading.events) return <LoadingSkeleton />;
    if (errors.events) return <Alert severity="warning" sx={{ mt: 1 }}>{errors.events}</Alert>;

    return (
      <Box sx={{ mt: 1 }}>
        {pitchEvents.length === 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            No pitch-level event data available yet for this player. Check the Matchup Splits tab for situational batting splits.
          </Alert>
        ) : (
          <InteractiveZoneChart
            pitchEvents={pitchEvents}
            title={`${perspective === 'pitcher' ? 'Pitch Arsenal' : 'Pitches Seen'} - Zone Analysis`}
            perspective={perspective}
            size={380}
            games={gameLog}
          />
        )}
      </Box>
    );
  };

  // ────── TAB 1: vs LH / vs RH ──────
  const HandednessSplitsTab = () => {
    if (loading.hand) return <LoadingSkeleton />;
    if (errors.hand) return <Alert severity="warning" sx={{ mt: 1 }}>{errors.hand}</Alert>;

    const renderHandPanel = (handData, label) => {
      if (!handData) return (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="caption" color="text.secondary">No data for {label}</Typography>
        </Box>
      );

      const pitchTypes = Object.entries(handData.pitchTypes || {})
        .sort((a, b) => b[1].count - a[1].count);
      const total = handData.totalPitches || pitchTypes.reduce((s, [, d]) => s + d.count, 0);

      return (
        <Box>
          {/* Strike zone with this hand's data */}
          {handData.pitchLocations && handData.pitchLocations.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <StrikeZonePlot
                pitchLocations={handData.pitchLocations}
                title={`vs ${label}`}
                size={200}
              />
            </Box>
          )}

          {/* Pitch type table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 0.3, fontSize: '0.75rem', fontWeight: 700 }}>Pitch</TableCell>
                  <TableCell align="right" sx={{ py: 0.3, fontSize: '0.75rem', fontWeight: 700 }}>#</TableCell>
                  <TableCell align="right" sx={{ py: 0.3, fontSize: '0.75rem', fontWeight: 700 }}>Usage</TableCell>
                  <TableCell align="right" sx={{ py: 0.3, fontSize: '0.75rem', fontWeight: 700 }}>Velo</TableCell>
                  <TableCell align="right" sx={{ py: 0.3, fontSize: '0.75rem', fontWeight: 700 }}>Whiff%</TableCell>
                  <TableCell align="right" sx={{ py: 0.3, fontSize: '0.75rem', fontWeight: 700 }}>AVG</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pitchTypes.map(([code, data]) => (
                  <PitchTypeRow key={code} code={code} data={data} totalPitches={total} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Zone summary */}
          {handData.zones && Object.keys(handData.zones).length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>Zone Distribution</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Object.entries(handData.zones)
                  .sort((a, b) => (b[1].pitches || b[1].count || 0) - (a[1].pitches || a[1].count || 0))
                  .slice(0, 9)
                  .map(([zone, data]) => (
                    <Chip
                      key={zone}
                      label={`Z${zone}: ${data.pitches || data.count || 0}`}
                      size="small"
                      sx={{ fontSize: '0.75rem', height: 20 }}
                    />
                  ))}
              </Box>
            </Box>
          )}
        </Box>
      );
    };

    return (
      <Box sx={{ mt: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent sx={{ pb: '12px !important' }}>
                <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                  vs Left-Handed {perspective === 'pitcher' ? 'Batters' : 'Pitchers'}
                </Typography>
                {renderHandPanel(vsHandL, 'LH')}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent sx={{ pb: '12px !important' }}>
                <Typography variant="subtitle2" fontWeight="bold" color="error" sx={{ mb: 1 }}>
                  vs Right-Handed {perspective === 'pitcher' ? 'Batters' : 'Pitchers'}
                </Typography>
                {renderHandPanel(vsHandR, 'RH')}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Side-by-side comparison highlight */}
        {vsHandL && vsHandR && (
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent sx={{ pb: '12px !important' }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Platoon Comparison</Typography>
              <PlatoonComparison left={vsHandL} right={vsHandR} perspective={perspective} />
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

  // ────── TAB 2: Game Log ──────
  const GameLogTab = () => {
    if (loading.games) return <LoadingSkeleton />;
    if (errors.games) return <Alert severity="warning" sx={{ mt: 1 }}>{errors.games}</Alert>;

    if (gameLog.length === 0) {
      return <Alert severity="info" sx={{ mt: 1 }}>Per-game pitch usage data is not available yet. Check the Matchup Splits tab for situational splits.</Alert>;
    }

    // Use pre-computed allGamePitchTypes from top level

    return (
      <Box sx={{ mt: 1 }}>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Opponent</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>H/A</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Total</TableCell>
                {allGamePitchTypes.map(pt => (
                  <TableCell key={pt} align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.3 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: PITCH_COLORS[pt] || '#888' }} />
                      {getPitchName(pt)}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {gameLog.map(g => (
                <TableRow key={g.gameId} hover>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{g.date}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{g.opponent}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    <Chip label={g.homeAway === 'home' ? 'H' : 'A'} size="small" sx={{ height: 18, fontSize: '0.75rem' }} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700 }}>{g.totalPitches}</TableCell>
                  {allGamePitchTypes.map(pt => {
                    const pd = g.pitchTypes?.[pt];
                    return (
                      <TableCell key={pt} align="right" sx={{ fontSize: '0.75rem' }}>
                        {pd ? (
                          <Box>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{pd.count}</Typography>
                            {pd.avgVelo > 0 && (
                              <Typography variant="caption" display="block" sx={{ fontSize: '0.75rem', opacity: 0.6 }}>
                                {pd.avgVelo}
                              </Typography>
                            )}
                          </Box>
                        ) : '-'}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pitch mix trend visualization */}
        {gameLog.length > 1 && (
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent sx={{ pb: '12px !important' }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Pitch Mix Trend</Typography>
              <PitchMixTrend games={gameLog} pitchTypes={allGamePitchTypes} />
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

  // ────── TAB 3: Matchup Splits (Modern UI) ──────
  const MacroSplitsTab = () => {
    if (loading.macro) return <LoadingSkeleton />;
    if (errors.macro) return <Alert severity="warning" sx={{ mt: 1 }}>{errors.macro}</Alert>;
    if (Object.keys(splitsByPath).length === 0) {
      return <Alert severity="info" sx={{ mt: 1 }}>No macro split data available.</Alert>;
    }

    // ── Data extraction ──
    const extractBattingLeaf = (val) => {
      const raw = val?.stats || val;
      const b = raw?.batting || raw;
      return {
        pa: b.plateAppearances || b.pa || b.PA || 0,
        atBats: b.atBats || b.AB || 0,
        hits: b.hits || b.H || 0,
        doubles: b.doubles || b['2B'] || 0,
        triples: b.triples || b['3B'] || 0,
        hr: b.homeRuns || b.hr || b.HR || 0,
        walks: b.baseOnBalls || b.walks || b.BB || 0,
        strikeouts: b.strikeOuts || b.strikeouts || b.K || 0,
        rbi: b.rbi || b.RBI || 0,
        hbp: b.hitByPitch || b.HBP || 0,
        sf: b.sacrificeFlies || b.SF || 0,
        obp: b.onBasePercentage || b.obp || b.OBP || null,
        slg: b.sluggingPercentage || b.slg || b.SLG || null,
        ops: b.ops || b.OPS || null,
        games: val?.games?.length || val?.gameCount || b.gamesPlayed || 0,
      };
    };

    const extractBatting = (val) => {
      const locKeys = ['home', 'away'];
      const hasLocationSplits = locKeys.some(k => val?.[k]?.stats || val?.[k]?.batting);
      let pa, atBats, hits, doubles, triples, hr, walks, strikeouts, rbi, hbp, sf, games;
      let preObp = null, preSlg = null, preOps = null;
      if (hasLocationSplits) {
        pa = 0; atBats = 0; hits = 0; doubles = 0; triples = 0; hr = 0; walks = 0; strikeouts = 0; rbi = 0; hbp = 0; sf = 0;
        const gameSet = new Set();
        for (const loc of locKeys) {
          if (!val[loc]) continue;
          const leaf = extractBattingLeaf(val[loc]);
          pa += leaf.pa; atBats += leaf.atBats; hits += leaf.hits;
          doubles += leaf.doubles; triples += leaf.triples;
          hr += leaf.hr; walks += leaf.walks; strikeouts += leaf.strikeouts; rbi += leaf.rbi;
          hbp += leaf.hbp; sf += leaf.sf;
          (val[loc]?.games || []).forEach(g => gameSet.add(g));
        }
        games = gameSet.size || 0;
      } else {
        const leaf = extractBattingLeaf(val);
        pa = leaf.pa; atBats = leaf.atBats; hits = leaf.hits;
        doubles = leaf.doubles; triples = leaf.triples;
        hr = leaf.hr; walks = leaf.walks; strikeouts = leaf.strikeouts; rbi = leaf.rbi;
        hbp = leaf.hbp; sf = leaf.sf;
        preObp = leaf.obp; preSlg = leaf.slg; preOps = leaf.ops;
        games = leaf.games;
      }
      const avg = atBats > 0 ? hits / atBats : null;
      const obp = preObp || (pa > 0 ? (hits + walks + hbp) / pa : null);
      const singles = hits - doubles - triples - hr;
      const totalBases = singles + 2 * doubles + 3 * triples + 4 * hr;
      const slg = preSlg || (atBats > 0 ? totalBases / atBats : null);
      const ops = preOps || (obp != null && slg != null ? obp + slg : null);
      const kRate = pa > 0 ? (strikeouts / pa * 100) : null;
      const bbRate = pa > 0 ? (walks / pa * 100) : null;
      return { pa, atBats, hits, hr, rbi, walks, strikeouts, avg, obp, slg, ops, kRate, bbRate, games };
    };

    // ── Pitching stat extraction (for pitcher by_location / vs_teams) ──
    // Precomputed play-by-play data stores IP as decimal numbers (e.g. 15.96)
    // On-the-fly builder stores IP as baseball notation strings (e.g. "5.2" = 5⅔)
    const parsePitchingIP = (ipVal) => {
      if (!ipVal && ipVal !== 0) return 0;
      // Numeric values from precomputed data are already decimal innings
      if (typeof ipVal === 'number') return ipVal;
      // String values from on-the-fly builder use baseball notation
      const s = String(ipVal);
      const [whole, frac] = s.split('.');
      return (parseInt(whole) || 0) + ((parseInt(frac) || 0) / 3);
    };

    // Convert decimal innings to baseball notation for display (5.333 → "5.1")
    const formatIPDisplay = (decimalIP) => {
      if (decimalIP == null || decimalIP === 0) return '0.0';
      const whole = Math.floor(decimalIP);
      const outs = Math.round((decimalIP - whole) * 3);
      return `${whole}.${outs}`;
    };

    const extractPitchingLeaf = (val) => {
      const raw = val?.stats || val;
      const p = raw?.pitching || raw;
      return {
        ip: parsePitchingIP(p?.inningsPitched),
        er: parseFloat(p?.earnedRuns) || 0,
        hits: parseFloat(p?.hits) || 0,
        bb: parseFloat(p?.baseOnBalls ?? p?.walks) || 0,
        k: parseFloat(p?.strikeOuts ?? p?.strikeouts) || 0,
        hr: parseFloat(p?.homeRuns) || 0,
        bf: parseFloat(p?.battersFaced) || 0,
        gs: parseFloat(p?.gamesStarted) || 0,
        w: parseFloat(p?.wins) || 0,
        l: parseFloat(p?.losses) || 0,
        games: val?.games?.length || val?.gameCount || parseFloat(p?.gamesPlayed) || 0,
      };
    };

    const extractPitching = (val) => {
      const locKeys = ['home', 'away'];
      const hasLocationSplits = locKeys.some(k => {
        const leaf = val?.[k];
        if (!leaf) return false;
        const pit = leaf?.stats?.pitching || leaf?.pitching;
        return pit && Object.keys(pit).length > 0;
      });

      let ip, er, hits, bb, k, hr, bf, gs, w, l, games;
      if (hasLocationSplits) {
        ip = 0; er = 0; hits = 0; bb = 0; k = 0; hr = 0; bf = 0; gs = 0; w = 0; l = 0;
        const gameSet = new Set();
        for (const loc of locKeys) {
          if (!val[loc]) continue;
          const leaf = extractPitchingLeaf(val[loc]);
          ip += leaf.ip; er += leaf.er; hits += leaf.hits; bb += leaf.bb;
          k += leaf.k; hr += leaf.hr; bf += leaf.bf; gs += leaf.gs;
          w += leaf.w; l += leaf.l;
          (val[loc]?.games || []).forEach(g => gameSet.add(g));
        }
        games = gameSet.size || 0;
      } else {
        const leaf = extractPitchingLeaf(val);
        ip = leaf.ip; er = leaf.er; hits = leaf.hits; bb = leaf.bb;
        k = leaf.k; hr = leaf.hr; bf = leaf.bf; gs = leaf.gs;
        w = leaf.w; l = leaf.l; games = leaf.games;
      }

      const era = ip > 0 ? (er * 9) / ip : null;
      const whip = ip > 0 ? (bb + hits) / ip : null;
      const k9 = ip > 0 ? (k * 9) / ip : null;
      const bb9 = ip > 0 ? (bb * 9) / ip : null;
      const kRate = bf > 0 ? (k / bf * 100) : null;
      return { games, gs, ip, w, l, era, k, bb, hr, whip, k9, bb9, kRate, bf };
    };

    // ── Batting-against extraction (pitcher matchup vs specific batters/handedness) ──
    // Converts pitching stats (battersFaced, hits, walks, K, HR) → batting-against line
    const extractBattingAgainstLeaf = (val) => {
      const raw = val?.stats || val;
      const p = raw?.pitching || raw;
      const bf = parseFloat(p?.battersFaced) || 0;
      const hits = parseFloat(p?.hits) || 0;
      const walks = parseFloat(p?.baseOnBalls ?? p?.walks) || 0;
      const k = parseFloat(p?.strikeOuts ?? p?.strikeouts) || 0;
      const hr = parseFloat(p?.homeRuns) || 0;
      const hbp = parseFloat(p?.hitBatsmen ?? p?.hitByPitch) || 0;
      const games = val?.games?.length || val?.gameCount || parseFloat(p?.gamesPlayed) || 0;
      return { bf, hits, walks, k, hr, hbp, games };
    };

    const extractBattingAgainst = (val) => {
      const locKeys = ['home', 'away'];
      const hasLocationSplits = locKeys.some(lk => {
        const leaf = val?.[lk];
        if (!leaf) return false;
        const pit = leaf?.stats?.pitching || leaf?.pitching;
        return pit && Object.keys(pit).length > 0;
      });

      let bf, hits, walks, k, hr, hbp, games;
      if (hasLocationSplits) {
        bf = 0; hits = 0; walks = 0; k = 0; hr = 0; hbp = 0;
        const gameSet = new Set();
        for (const loc of locKeys) {
          if (!val[loc]) continue;
          const leaf = extractBattingAgainstLeaf(val[loc]);
          bf += leaf.bf; hits += leaf.hits; walks += leaf.walks;
          k += leaf.k; hr += leaf.hr; hbp += leaf.hbp;
          (val[loc]?.games || []).forEach(g => gameSet.add(g));
        }
        games = gameSet.size || 0;
      } else {
        const leaf = extractBattingAgainstLeaf(val);
        bf = leaf.bf; hits = leaf.hits; walks = leaf.walks;
        k = leaf.k; hr = leaf.hr; hbp = leaf.hbp; games = leaf.games;
      }

      const pa = bf;
      const atBats = Math.max(0, pa - walks - hbp);
      const avg = atBats > 0 ? hits / atBats : null;
      const obp = pa > 0 ? (hits + walks + hbp) / pa : null;
      const kRate = pa > 0 ? (k / pa * 100) : null;
      const bbRate = pa > 0 ? (walks / pa * 100) : null;
      return { games, pa, atBats, hits, hr, k, walks, avg, obp, kRate, bbRate };
    };

    // Determines if a section should show traditional pitching stats (IP/ERA/WHIP)
    const isPitchingSection = (sectionKey) => {
      return perspective === 'pitcher' && ['by_location', 'vs_teams'].includes(sectionKey);
    };

    // Determines if a section should show batting-against stats (PA/AB/AVG/OBP)
    const isPitcherMatchupSection = (sectionKey) => {
      return perspective === 'pitcher' && ['vs_batters', 'vs_handedness'].includes(sectionKey);
    };

    // ── OPS color helper ──
    const getOpsColor = (ops) => {
      if (ops == null) return theme.palette.text.secondary;
      if (ops >= 1.000) return '#ff6b35';
      if (ops >= .900) return '#e74c3c';
      if (ops >= .800) return '#f39c12';
      if (ops >= .700) return '#27ae60';
      if (ops >= .600) return theme.palette.text.primary;
      return theme.palette.text.secondary;
    };

    const getOpsBarWidth = (ops) => {
      if (ops == null) return 0;
      return Math.min(Math.max((ops / 1.200) * 100, 2), 100);
    };

    // ── ERA color helper (lower is better) ──
    const getEraColor = (era) => {
      if (era == null) return theme.palette.text.secondary;
      if (era <= 2.00) return '#ff6b35';
      if (era <= 3.00) return '#e74c3c';
      if (era <= 3.50) return '#f39c12';
      if (era <= 4.00) return '#27ae60';
      if (era <= 5.00) return theme.palette.text.primary;
      return theme.palette.text.secondary;
    };

    const getEraBarWidth = (era) => {
      if (era == null) return 0;
      // Invert: lower ERA = wider bar. Scale 0-7.
      return Math.min(Math.max(((7 - era) / 7) * 100, 2), 100);
    };

    // ── Section config (perspective-aware) ──
    const SECTIONS = [
      { key: 'by_location', label: 'Home / Away', icon: <Home sx={{ fontSize: 16 }} />, minPa: 0 },
      { key: 'vs_handedness', label: 'vs Hand', icon: <SwapHoriz sx={{ fontSize: 16 }} />, minPa: 0 },
      { key: 'vs_teams', label: 'vs Teams', icon: <Groups sx={{ fontSize: 16 }} />, minPa: 0, searchable: true },
      { key: 'vs_pitchers', label: 'vs Pitchers', icon: <SportsBaseball sx={{ fontSize: 16 }} />, minPa: 0, searchable: true, collapsible: true },
      ...(perspective === 'pitcher' ? [
        { key: 'vs_batters', label: 'vs Batters', icon: <Person sx={{ fontSize: 16 }} />, minPa: 0, searchable: true, collapsible: true },
      ] : []),
      { key: 'by_count', label: 'By Count', icon: <Shuffle sx={{ fontSize: 16 }} />, minPa: 0 },
    ];

    // The Splits Matrix above (single-season, pitch-event derived) already
    // covers Home/Away, vs Hand, and By Count with baseline-colored cells —
    // hide those duplicated old-style sections and keep only the opponent-level
    // splits (vs Teams / vs Pitchers / vs Batters) the matrix can't show.
    // In career/all-time mode the matrix is unavailable, so show everything.
    const matrixShown = !isCareer && Array.isArray(sharedPitchEvents) && sharedPitchEvents.length > 0;
    const MATRIX_COVERED = ['by_location', 'vs_handedness', 'by_count'];
    const availableSections = SECTIONS
      .filter(s => !(matrixShown && MATRIX_COVERED.includes(s.key)))
      .filter(s => splitsByPath[s.key] && Object.keys(splitsByPath[s.key]).length > 0);

    // ── Stat columns definition ──
    const BATTING_COLS = [
      { key: 'games', label: 'G', width: 40 },
      { key: 'pa', label: 'PA', width: 45 },
      { key: 'avg', label: 'AVG', width: 55, fmt: formatAvg, bold: true },
      { key: 'obp', label: 'OBP', width: 50, fmt: formatAvg },
      { key: 'slg', label: 'SLG', width: 50, fmt: formatAvg },
      { key: 'ops', label: 'OPS', width: 55, fmt: formatAvg, bold: true, color: true },
      { key: 'hr', label: 'HR', width: 40 },
      { key: 'rbi', label: 'RBI', width: 40 },
      { key: 'kRate', label: 'K%', width: 48, fmt: (v) => v != null ? v.toFixed(1) + '%' : '-' },
      { key: 'bbRate', label: 'BB%', width: 48, fmt: (v) => v != null ? v.toFixed(1) + '%' : '-' },
    ];

    // Batter matchup columns (vs specific pitchers — AB-centric detail)
    const BATTER_MATCHUP_COLS = [
      { key: 'games', label: 'G', width: 40 },
      { key: 'pa', label: 'PA', width: 45 },
      { key: 'atBats', label: 'AB', width: 45 },
      { key: 'avg', label: 'AVG', width: 55, fmt: formatAvg, bold: true },
      { key: 'obp', label: 'OBP', width: 50, fmt: formatAvg },
      { key: 'slg', label: 'SLG', width: 50, fmt: formatAvg },
      { key: 'ops', label: 'OPS', width: 55, fmt: formatAvg, bold: true, color: true },
      { key: 'hits', label: 'H', width: 40 },
      { key: 'hr', label: 'HR', width: 40 },
      { key: 'strikeouts', label: 'K', width: 40 },
      { key: 'walks', label: 'BB', width: 40 },
    ];

    const PITCHING_COLS = [
      { key: 'games', label: 'G', width: 40 },
      { key: 'gs', label: 'GS', width: 40 },
      { key: 'ip', label: 'IP', width: 48, fmt: (v) => v != null ? formatIPDisplay(v) : '-' },
      { key: 'era', label: 'ERA', width: 55, fmt: (v) => v != null ? v.toFixed(2) : '-', bold: true, color: 'era' },
      { key: 'whip', label: 'WHIP', width: 55, fmt: (v) => v != null ? v.toFixed(2) : '-', bold: true },
      { key: 'k', label: 'K', width: 40 },
      { key: 'bb', label: 'BB', width: 40 },
      { key: 'hr', label: 'HR', width: 40 },
      { key: 'k9', label: 'K/9', width: 48, fmt: (v) => v != null ? v.toFixed(1) : '-' },
      { key: 'bb9', label: 'BB/9', width: 48, fmt: (v) => v != null ? v.toFixed(1) : '-' },
    ];

    // Batting-against columns (pitcher matchup perspective — PA/AB based)
    const BATTING_AGAINST_COLS = [
      { key: 'games', label: 'G', width: 40 },
      { key: 'pa', label: 'PA', width: 45 },
      { key: 'atBats', label: 'AB', width: 45 },
      { key: 'avg', label: 'AVG', width: 55, fmt: formatAvg, bold: true },
      { key: 'obp', label: 'OBP', width: 50, fmt: formatAvg },
      { key: 'hits', label: 'H', width: 40 },
      { key: 'hr', label: 'HR', width: 40 },
      { key: 'k', label: 'K', width: 40 },
      { key: 'walks', label: 'BB', width: 40 },
      { key: 'kRate', label: 'K%', width: 48, fmt: (v) => v != null ? v.toFixed(1) + '%' : '-' },
      { key: 'bbRate', label: 'BB%', width: 48, fmt: (v) => v != null ? v.toFixed(1) + '%' : '-' },
    ];

    const getStatCols = (sectionKey) => {
      if (isPitcherMatchupSection(sectionKey)) return BATTING_AGAINST_COLS;
      if (isPitchingSection(sectionKey)) return PITCHING_COLS;
      // Batter matchup sections (vs_pitchers, vs_handedness) get AB-detail columns
      if (perspective === 'batter' && ['vs_pitchers', 'vs_handedness'].includes(sectionKey)) return BATTER_MATCHUP_COLS;
      return BATTING_COLS;
    };
    const STAT_COLS = BATTING_COLS; // default for non-section-specific uses

    // ── Sort handler ──
    const handleSplitSort = (colKey) => {
      if (splitSortBy === colKey) {
        setSplitSortOrder(o => o === 'desc' ? 'asc' : 'desc');
      } else {
        setSplitSortBy(colKey);
        // Default desc for counting stats, asc for rate stats where lower is better
        setSplitSortOrder('desc');
      }
    };

    // ── Prepare entries for a section ──
    const prepareEntries = (sectionKey) => {
      const data = splitsByPath[sectionKey];
      if (!data) return [];
      const extractor = isPitcherMatchupSection(sectionKey) ? extractBattingAgainst
        : isPitchingSection(sectionKey) ? extractPitching : extractBatting;
      let entries = Object.entries(data)
        .filter(([k, v]) => v && typeof v === 'object')
        .map(([k, v]) => ({ key: k, label: k.replace(/_/g, ' '), ...extractor(v) }));

      // Search filter
      if (splitSearch && (sectionKey === 'vs_pitchers' || sectionKey === 'vs_teams' || sectionKey === 'vs_batters')) {
        const q = splitSearch.toLowerCase();
        entries = entries.filter(e => e.label.toLowerCase().includes(q));
      }

      // Sort
      entries.sort((a, b) => {
        const aVal = a[splitSortBy] ?? -Infinity;
        const bVal = b[splitSortBy] ?? -Infinity;
        const cmp = (typeof aVal === 'number' && typeof bVal === 'number') ? aVal - bVal : String(aVal).localeCompare(String(bVal));
        return splitSortOrder === 'desc' ? -cmp : cmp;
      });

      return entries;
    };

    // ── Sortable Header Cell ──
    const SortHeader = ({ col }) => {
      const isActive = splitSortBy === col.key;
      return (
        <TableCell
          align="right"
          sx={{
            width: col.width, minWidth: col.width, py: 0.5, px: 0.75,
            cursor: 'pointer', userSelect: 'none',
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
            ...(isActive && { bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }),
          }}
          onClick={() => handleSplitSort(col.key)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: 0.3 }}>
              {col.label}
            </Typography>
            {isActive && (
              splitSortOrder === 'desc'
                ? <ArrowDownward sx={{ fontSize: '0.75rem' }} />
                : <ArrowUpward sx={{ fontSize: '0.75rem' }} />
            )}
          </Box>
        </TableCell>
      );
    };

    // ── Data Row ──
    const SplitRow = ({ entry, rank, cols }) => {
      const useCols = cols || STAT_COLS;
      const isPitching = useCols === PITCHING_COLS;
      const isBattingAgainst = useCols === BATTING_AGAINST_COLS;
      return (
      <TableRow hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
        <TableCell sx={{ py: 0.75, px: 1, minWidth: 120 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 16, textAlign: 'right' }}>
              {rank}
            </Typography>
            <Tooltip title={entry.label} placement="top-start" arrow>
              <Typography variant="body2" sx={{
                fontSize: '0.75rem', fontWeight: 600,
                maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {entry.label}
              </Typography>
            </Tooltip>
          </Box>
        </TableCell>
        {useCols.map(col => {
          const val = entry[col.key];
          const display = col.fmt ? col.fmt(val) : (val != null ? val : '-');
          const isOps = col.color === true && col.key === 'ops';
          const isEra = col.color === 'era' && col.key === 'era';
          return (
            <TableCell key={col.key} align="right" sx={{
              py: 0.75, px: 0.75,
              fontSize: '0.75rem',
              fontWeight: col.bold ? 700 : 400,
              color: isOps ? getOpsColor(val) : isEra ? getEraColor(val) : 'inherit',
            }}>
              {display}
            </TableCell>
          );
        })}
        {/* Performance mini-bar (OPS for batting/batting-against, ERA for pitching) */}
        <TableCell sx={{ py: 0.75, px: 0.75, width: 60 }}>
          <Box sx={{
            height: 6, borderRadius: 3, bgcolor: alpha(theme.palette.divider, 0.15),
            overflow: 'hidden', minWidth: 40,
          }}>
            {(() => {
              // Batting-against: use OBP as performance bar (lower is better for pitcher)
              const barVal = isPitching ? entry.era : (isBattingAgainst ? entry.obp : entry.ops);
              const barWidth = isPitching ? getEraBarWidth(barVal) : getOpsBarWidth(barVal);
              const barColor = isPitching ? getEraColor(barVal) : getOpsColor(barVal);
              return (
                <Box sx={{
                  height: '100%', borderRadius: 3,
                  width: `${barWidth}%`,
                  bgcolor: barColor,
                  transition: 'width 0.3s ease',
                }} />
              );
            })()}
          </Box>
        </TableCell>
      </TableRow>
    );
    };

    // ── Render a full section ──
    const renderSection = (section) => {
      const entries = prepareEntries(section.key);
      if (entries.length === 0) return null;
      const isExpanded = expandedSections[section.key] !== false;
      const isCollapsible = section.collapsible || entries.length > 15;
      const sectionCols = getStatCols(section.key);
      const isPitching = sectionCols === PITCHING_COLS;

      // For large lists, show limited by default
      const defaultShowCount = section.key === 'vs_pitchers' ? 20 : (section.key === 'vs_teams' ? 30 : entries.length);
      const showAll = section.key === 'vs_pitchers' ? showAllPitchers : (section.key === 'vs_teams' ? showAllTeams : true);
      const displayEntries = showAll ? entries : entries.slice(0, defaultShowCount);
      const hasMore = entries.length > defaultShowCount && !showAll;

      return (
        <Card key={section.key} variant="outlined" sx={{
          mb: 1.5, borderRadius: 2, overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        }}>
          {/* Section Header */}
          <Box
            onClick={() => isCollapsible && setExpandedSections(prev => ({ ...prev, [section.key]: !prev[section.key] }))}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              px: 1.5, py: 1,
              bgcolor: isDark ? alpha('#fff', 0.02) : alpha('#000', 0.015),
              cursor: isCollapsible ? 'pointer' : 'default',
              '&:hover': isCollapsible ? { bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.03) } : {},
              borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.primary.main }}>
              {section.icon}
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', flex: 1 }}>
              {section.label}
            </Typography>
            <Chip
              label={entries.length}
              size="small"
              sx={{ height: 20, fontSize: '0.75rem', fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }}
            />
            {isCollapsible && (
              isExpanded ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />
            )}
          </Box>

          <Collapse in={isExpanded} timeout="auto">
            {/* Search bar for large sections */}
            {section.searchable && entries.length > 5 && (
              <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
                <TextField
                  size="small"
                  placeholder={`Search ${section.label.toLowerCase()}...`}
                  value={splitSearch}
                  onChange={(e) => setSplitSearch(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    sx: { fontSize: '0.75rem', height: 32, borderRadius: 1.5 },
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02) } }}
                />
              </Box>
            )}

            {/* Data Table */}
            <TableContainer sx={{ maxHeight: section.key === 'vs_pitchers' ? 500 : undefined }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{
                      fontWeight: 700, fontSize: '0.75rem', py: 0.5, px: 1,
                      bgcolor: isDark ? alpha('#fff', 0.03) : '#fafafa',
                      minWidth: 120,
                    }}>
                      Split
                    </TableCell>
                    {sectionCols.map(col => <SortHeader key={col.key} col={col} />)}
                    <TableCell sx={{
                      width: 60, py: 0.5, px: 0.75,
                      bgcolor: isDark ? alpha('#fff', 0.03) : '#fafafa',
                      fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary',
                    }}>
                      {isPitching ? 'ERA' : 'OPS'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayEntries.map((entry, i) => (
                    <SplitRow key={entry.key} entry={entry} rank={i + 1} cols={sectionCols} />
                  ))}
                  {entries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={sectionCols.length + 2} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                        <Typography variant="caption">No results{splitSearch ? ` for "${splitSearch}"` : ''}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Show More / Less */}
            {hasMore && (
              <Box
                onClick={() => section.key === 'vs_pitchers' ? setShowAllPitchers(true) : setShowAllTeams(true)}
                sx={{
                  textAlign: 'center', py: 0.75, cursor: 'pointer',
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                }}
              >
                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  Show all {entries.length} {section.label.toLowerCase()} ▾
                </Typography>
              </Box>
            )}
            {showAll && entries.length > defaultShowCount && (
              <Box
                onClick={() => section.key === 'vs_pitchers' ? setShowAllPitchers(false) : setShowAllTeams(false)}
                sx={{
                  textAlign: 'center', py: 0.75, cursor: 'pointer',
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Show fewer ▴
                </Typography>
              </Box>
            )}
          </Collapse>
        </Card>
      );
    };

    // ── Summary Stats Bar ──
    const locationData = splitsByPath.by_location;
    const isPitcherLoc = isPitchingSection('by_location');
    const homeStats = locationData?.home ? (isPitcherLoc ? extractPitching(locationData.home) : extractBatting(locationData.home)) : null;
    const awayStats = locationData?.away ? (isPitcherLoc ? extractPitching(locationData.away) : extractBatting(locationData.away)) : null;

    // Summary card renderer (works for both batting and pitching)
    const renderLocationCard = (stats, label, icon, iconColor) => {
      if (!stats) return null;
      return (
        <Card variant="outlined" sx={{ flex: 1, minWidth: 160, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              {icon}
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>{label}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline' }}>
              {isPitcherLoc ? (
                <>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', lineHeight: 1, color: getEraColor(stats.era) }}>
                      {stats.era != null ? stats.era.toFixed(2) : '-'}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>ERA</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>{stats.whip != null ? stats.whip.toFixed(2) : '-'}</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>WHIP</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>{stats.k || 0}</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>K</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>{stats.ip != null ? stats.ip.toFixed(1) : '-'}</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>IP</Typography>
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', lineHeight: 1, color: getOpsColor(stats.ops) }}>
                      {formatAvg(stats.ops)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>OPS</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>{formatAvg(stats.avg)}</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>AVG</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>{stats.hr}</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>HR</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>{stats.pa}</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>PA</Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      );
    };

    return (
      <Box sx={{ mt: 1 }}>
        {/* Quick Summary Cards — hidden when the Splits Matrix already shows Home/Away */}
        {!matrixShown && (homeStats || awayStats) && (
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            {renderLocationCard(homeStats, 'Home', <Home sx={{ fontSize: 14, color: theme.palette.primary.main }} />)}
            {renderLocationCard(awayStats, 'Away', <Flight sx={{ fontSize: 14, color: theme.palette.warning.main }} />)}
          </Box>
        )}

        {/* Section Filter Chips */}
        <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
          <Chip
            label="All Splits"
            size="small"
            variant={activeSection === 'all' ? 'filled' : 'outlined'}
            color={activeSection === 'all' ? 'primary' : 'default'}
            onClick={() => setActiveSection('all')}
            sx={{ fontWeight: 600, fontSize: '0.75rem', height: 28 }}
          />
          {availableSections.map(s => (
            <Chip
              key={s.key}
              icon={s.icon}
              label={s.label}
              size="small"
              variant={activeSection === s.key ? 'filled' : 'outlined'}
              color={activeSection === s.key ? 'primary' : 'default'}
              onClick={() => { setActiveSection(s.key); setSplitSearch(''); }}
              sx={{ fontWeight: 600, fontSize: '0.75rem', height: 28, '& .MuiChip-icon': { fontSize: 14 } }}
            />
          ))}
        </Box>

        {/* Sections */}
        {availableSections
          .filter(s => activeSection === 'all' || activeSection === s.key)
          .map(s => renderSection(s))}
      </Box>
    );
  };

  const dateRangeLabel = splitsDateRange === 'alltime' ? 'All Time' : String(season);

  return (
    <Box>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02), px: 2, pt: 1.5, pb: 1.5 }}>
          {/* Title row */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            <Box>
              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                Splits Explorer
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {playerName} • {team} • {dateRangeLabel}
                {isCareer && splitsTotalGames > 0 && ` — ${splitsTotalGames} games`}
              </Typography>
            </Box>
          </Box>

          {/* Date Range row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.7rem', minWidth: 36 }}>RANGE</Typography>
            <Chip
              label={`📅 ${season} Season`}
              size="small"
              variant={splitsDateRange === 'season' ? 'filled' : 'outlined'}
              color={splitsDateRange === 'season' ? 'primary' : 'default'}
              onClick={() => setSplitsDateRange('season')}
              sx={{ fontWeight: 600, fontSize: '0.75rem', height: 26 }}
            />
            <Chip
              label="🕐 All Time"
              size="small"
              variant={splitsDateRange === 'alltime' ? 'filled' : 'outlined'}
              color={splitsDateRange === 'alltime' ? 'secondary' : 'default'}
              onClick={() => setSplitsDateRange('alltime')}
              sx={{ fontWeight: 600, fontSize: '0.75rem', height: 26 }}
            />
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          {!isCareer && (
            <SplitsMatrix
              pitchEvents={sharedPitchEvents}
              perspective={perspective}
              season={season}
              gameTypeFilter={gameTypeFilter}
            />
          )}
          <MacroSplitsTab />
        </Box>
      </Paper>
    </Box>
  );
};

// ────────── Helper Components ──────────

/** Loading skeleton */
const LoadingSkeleton = () => (
  <Box sx={{ mt: 1 }}>
    <Skeleton variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
    <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
  </Box>
);

/** Format batting average / rate stat — handles OPS > 1 correctly */
const formatAvg = (val) => {
  if (val == null || isNaN(val)) return '-';
  if (val >= 1) return val.toFixed(3);   // OPS can exceed 1.000 — show full value e.g. 1.234
  if (val < 0) return val.toFixed(3);
  return val.toFixed(3).substring(1);    // Strip leading 0: .345
};

/** Platoon comparison table */
const PlatoonComparison = ({ left, right, perspective }) => {
  // Gather all pitch types from both hands
  const allTypes = new Set([
    ...Object.keys(left?.pitchTypes || {}),
    ...Object.keys(right?.pitchTypes || {}),
  ]);

  const compare = (ptCode) => {
    const l = left?.pitchTypes?.[ptCode] || {};
    const r = right?.pitchTypes?.[ptCode] || {};
    return { code: ptCode, l, r };
  };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Pitch</TableCell>
            <TableCell align="center" colSpan={3} sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'primary.main' }}>
              vs LH
            </TableCell>
            <TableCell align="center" colSpan={3} sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'error.main' }}>
              vs RH
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontSize: '0.75rem' }}></TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Usage</TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Velo</TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Whiff%</TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Usage</TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Velo</TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Whiff%</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...allTypes].map(pt => {
            const { l, r } = compare(pt);
            const lTotal = left?.totalPitches || 1;
            const rTotal = right?.totalPitches || 1;
            return (
              <TableRow key={pt} hover>
                <TableCell sx={{ py: 0.3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PITCH_COLORS[pt] || '#888' }} />
                    <Typography variant="caption">{getPitchName(pt)}</Typography>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ py: 0.3 }}><Typography variant="caption">{l.count ? ((l.count / lTotal) * 100).toFixed(1) + '%' : '-'}</Typography></TableCell>
                <TableCell align="right" sx={{ py: 0.3 }}><Typography variant="caption">{l.avgVelo > 0 ? l.avgVelo.toFixed(1) : '-'}</Typography></TableCell>
                <TableCell align="right" sx={{ py: 0.3 }}><Typography variant="caption">{l.whiffRate != null ? l.whiffRate.toFixed(1) + '%' : '-'}</Typography></TableCell>
                <TableCell align="right" sx={{ py: 0.3 }}><Typography variant="caption">{r.count ? ((r.count / rTotal) * 100).toFixed(1) + '%' : '-'}</Typography></TableCell>
                <TableCell align="right" sx={{ py: 0.3 }}><Typography variant="caption">{r.avgVelo > 0 ? r.avgVelo.toFixed(1) : '-'}</Typography></TableCell>
                <TableCell align="right" sx={{ py: 0.3 }}><Typography variant="caption">{r.whiffRate != null ? r.whiffRate.toFixed(1) + '%' : '-'}</Typography></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/** Simple pitch mix trend - stacked usage bars per game */
const PitchMixTrend = ({ games, pitchTypes }) => {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end', height: 80, overflowX: 'auto' }}>
      {games.map(g => {
        const total = g.totalPitches || 1;
        return (
          <Box key={g.gameId} sx={{ display: 'flex', flexDirection: 'column', minWidth: 18, height: '100%', justifyContent: 'flex-end' }}>
            {pitchTypes.map(pt => {
              const count = g.pitchTypes?.[pt]?.count || 0;
              const pct = (count / total) * 100;
              if (pct < 1) return null;
              return (
                <Box
                  key={pt}
                  sx={{
                    width: '100%',
                    height: `${pct}%`,
                    bgcolor: PITCH_COLORS[pt] || '#888',
                    opacity: 0.8,
                    '&:hover': { opacity: 1 },
                    minHeight: pct > 0 ? 2 : 0,
                  }}
                  title={`${pt}: ${count} (${pct.toFixed(0)}%)`}
                />
              );
            })}
            <Typography variant="caption" sx={{ fontSize: '0.75rem', textAlign: 'center', mt: 0.2, opacity: 0.5 }}>
              {g.date?.slice(5) || '?'}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default SplitsExplorer;
